import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LoginPopupBackground from "../../assets/imgs/FullAccessGrantBackground.png";

const ConfirmButton = styled.button({
  width: "100%",
  padding: "var(--spacing10) var(--spacing16)",
  border: "none",
  backgroundColor: "var(--alias-button-default)",
  color: "var(--alias-text-on-special-primary)",
  fontFamily: "var(--body-b2-14-medium-font)",
  fontWeight: "var(--body-b2-14-medium-weight)",
  fontSize: "var(--body-b2-14-medium-size)",
  lineHeight: "var(--body-b2-14-medium-line-height)",
  letterSpacing: "var(--body-b2-14-medium-tracking)",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "var(--alias-button-hover)",
  },
});

interface WelcomeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onClickConfirm: () => void;
  dialogTitle?: string;
  dialogBodyHeader?: string;
  dialogBodyContent?: string;
  dialogConfirmText?: string;
}

/**
 * Welcome dialog shown after CDC account is successfully connected.
 * Prompts user to re-login for full áccess.
 *
 * @see Figma: node 29:1095 in Imagio Color Finder 2.0
 * @see Ticket: CX-1014, CX-1317, CX-1318
 */
export const WelcomeDialog = ({
  isOpen,
  onClose,
  onClickConfirm,
  dialogTitle = "Welcome to Imagio®",
  dialogBodyHeader = "",
  dialogBodyContent = "",
  dialogConfirmText = "",
}: WelcomeDialogProps) => {
  const { t } = useTranslation();
  return (
    <Dialog
      id="WelcomeDialog"
      data-cy="WelcomeDialog"
      sx={{
        "& .MuiPaper-root": {
          width: "466px",
          maxWidth: "466px",
          overflow: "hidden",
          backgroundColor: "var(--alias-surface-lowest)",
          boxShadow:
            "0px 4px 12px 0px rgba(0, 0, 0, 0.04), 0px 4px 12px 1px rgba(0, 0, 0, 0.04)",
        },
      }}
      onClose={onClose}
      open={isOpen}
    >
      <Box
        sx={{
          height: "193px",
          width: "100%",
          backgroundImage: `url(${LoginPopupBackground})`,
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      />
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: "var(--spacing20)",
          top: "var(--spacing18)",
          color: "var(--alias-icon-on-special-primary)",
          padding: 0,
          "& .MuiSvgIcon-root": { fontSize: "var(--spacing20)" },
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ padding: "var(--spacing20)", overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing24)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing16)",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing16)",
                alignItems: "center",
                textAlign: "center",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--title-t1-20-medium-font)",
                  fontWeight: "var(--title-t1-20-medium-weight)",
                  fontSize: "var(--title-t1-20-medium-size)",
                  lineHeight: "var(--title-t1-20-medium-line-height)",
                  letterSpacing: "var(--title-t1-20-medium-tracking)",
                  color: "var(--alias-text-on-white-primary)",
                }}
              >
                {dialogTitle}
              </Typography>
              <Box
                sx={{
                  fontFamily: "var(--body-b2-14-regular-font)",
                  fontWeight: "var(--body-b2-14-regular-weight)",
                  fontSize: "var(--body-b2-14-regular-size)",
                  lineHeight: "var(--body-b2-14-regular-line-height)",
                  letterSpacing: "var(--body-b2-14-regular-tracking)",
                  color: "var(--alias-text-on-white-secondary)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "pre-line",
                  }}
                >
                  {dialogBodyHeader || t("header_welcome_dialog_title")}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    color: "inherit",
                    mt: "var(--spacing14)",
                  }}
                >
                  {dialogBodyContent || t("header_welcome_dialog_content")}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing12)",
            }}
          >
            <ConfirmButton
              onClick={() => {
                onClickConfirm();
              }}
            >
              {dialogConfirmText || t("header_welcome_dialog_confirm")}
            </ConfirmButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
